/**
 * faceBlurRenderer.ts
 *
 * Utility for applying face blur effects to images/videos on canvas.
 * Used by CanvasStage to render elements with face blur enabled.
 */

import {
  detectFaces,
  loadFaceDetector,
  applyFaceBlur,
  applyFacePixelate,
  FaceTracker,
} from '../../engine/faceDetection';
import type { DetectedFace } from '../../engine/faceDetection';
import type { FaceBlurSettings } from '../../types';

// Re-export DetectedFace for consumers
export type { DetectedFace };

// Cache for detected faces per element
const faceCache = new Map<string, {
  faces: DetectedFace[];
  timestamp: number;
  sourceHash: string;
}>();

// Cache for processed images
const processedImageCache = new Map<string, {
  canvas: HTMLCanvasElement;
  settings: Partial<FaceBlurSettings>;
  timestamp: number;
}>();

// Video processing state per element
const videoStateCache = new Map<string, {
  canvas: HTMLCanvasElement;
  tracker: FaceTracker;
  faces: DetectedFace[];
  lastDetectTs: number;
  missingFrames: number;
  detectCanvas?: HTMLCanvasElement;
}>();

// Cache duration in ms (faces don't change often for static images)
const CACHE_DURATION = 30000; // 30 seconds
const VIDEO_DETECT_INTERVAL_MS = 120; // ~8 FPS face detection
const VIDEO_MAX_DETECT_SIZE = 512; // Downscale for detection performance
const VIDEO_MAX_MISSING_FRAMES = 8;

function getSourceSize(source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement): { width: number; height: number } {
  if (source instanceof HTMLCanvasElement) {
    return { width: source.width, height: source.height };
  }
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth || source.width, height: source.videoHeight || source.height };
  }
  return { width: source.naturalWidth || source.width, height: source.naturalHeight || source.height };
}

/**
 * Generate a simple hash for source comparison
 */
function hashSource(src: string): string {
  // Simple hash for cache invalidation
  let hash = 0;
  for (let i = 0; i < Math.min(src.length, 100); i++) {
    hash = ((hash << 5) - hash) + src.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

/**
 * Get cached faces or detect new ones
 */
export async function getCachedFaces(
  elementId: string,
  source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  sourceSrc: string
): Promise<DetectedFace[]> {
  const sourceHash = hashSource(sourceSrc);
  const cached = faceCache.get(elementId);
  const now = Date.now();

  // Return cached if valid
  if (cached && cached.sourceHash === sourceHash && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.faces;
  }

  // Ensure detector is loaded
  await loadFaceDetector();

  // Detect faces
  const faces = await detectFaces(source);

  // Cache results
  faceCache.set(elementId, {
    faces,
    timestamp: now,
    sourceHash,
  });

  return faces;
}

/**
 * Apply face blur to a canvas context
 */
export function renderFaceBlurToContext(
  ctx: CanvasRenderingContext2D,
  faces: DetectedFace[],
  settings: Partial<FaceBlurSettings>,
  selectedFaces?: string[]
): void {
  if (!settings.enabled || faces.length === 0) return;

  const mode = settings.mode || 'blur';
  const intensity = settings.intensity || 20;
  const padding = settings.padding || 10;
  const shape = settings.shape || 'ellipse';

  // Filter faces if specific ones selected
  const facesToBlur = selectedFaces && selectedFaces.length > 0
    ? faces.filter(f => selectedFaces.includes(f.id) || selectedFaces.includes(f.trackId || ''))
    : faces;

  if (facesToBlur.length === 0) return;

  switch (mode) {
    case 'blur':
      applyFaceBlur(ctx, facesToBlur, intensity, padding);
      break;

    case 'pixelate':
      applyFacePixelate(ctx, facesToBlur, intensity, padding);
      break;

    case 'box':
      // Draw solid boxes over faces
      ctx.fillStyle = settings.shape === 'ellipse' ? '#000000' : '#1a1a1a';
      for (const face of facesToBlur) {
        const { x, y, width, height } = face.box;
        const px = x - padding;
        const py = y - padding;
        const pw = width + padding * 2;
        const ph = height + padding * 2;

        if (shape === 'ellipse') {
          ctx.beginPath();
          ctx.ellipse(px + pw / 2, py + ph / 2, pw / 2, ph / 2, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(px, py, pw, ph);
        }
      }
      break;

    case 'emoji':
      // Draw emoji over faces
      for (const face of facesToBlur) {
        const { x, y, width, height } = face.box;
        const size = Math.max(width, height) + padding * 2;
        const cx = x + width / 2;
        const cy = y + height / 2;

        ctx.font = `${size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('😊', cx, cy);
      }
      break;
  }
}

/**
 * Process an image with face blur and return a canvas
 * This is used for static images where we can pre-process
 */
export async function processImageWithFaceBlur(
  elementId: string,
  img: HTMLImageElement,
  settings: Partial<FaceBlurSettings>
): Promise<HTMLCanvasElement | null> {
  if (!settings.enabled) return null;

  // Check cache
  const cached = processedImageCache.get(elementId);
  const now = Date.now();

  if (cached &&
      JSON.stringify(cached.settings) === JSON.stringify(settings) &&
      (now - cached.timestamp) < CACHE_DURATION) {
    return cached.canvas;
  }

  try {
    // Detect faces
    const faces = await getCachedFaces(elementId, img, img.src);

    if (faces.length === 0) {
      // No faces detected, no processing needed
      return null;
    }

    // Create canvas with image
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    // Draw original image
    ctx.drawImage(img, 0, 0);

    // Apply face blur
    renderFaceBlurToContext(ctx, faces, settings, settings.selectedFaces);

    // Cache result
    processedImageCache.set(elementId, {
      canvas,
      settings: { ...settings },
      timestamp: now,
    });

    return canvas;
  } catch (error) {
    console.error('Error processing image with face blur:', error);
    return null;
  }
}

/**
 * Process a video frame with face blur and return a canvas.
 * Uses lightweight tracking + detection throttling for performance.
 */
export async function processVideoFrameWithFaceBlur(
  elementId: string,
  source: HTMLVideoElement | HTMLCanvasElement,
  settings: Partial<FaceBlurSettings>
): Promise<HTMLCanvasElement | null> {
  if (!settings.enabled) return null;

  const { width, height } = getSourceSize(source);
  if (!width || !height) return null;

  let state = videoStateCache.get(elementId);
  if (!state) {
    state = {
      canvas: document.createElement('canvas'),
      tracker: new FaceTracker(),
      faces: [],
      lastDetectTs: 0,
      missingFrames: 0,
    };
    videoStateCache.set(elementId, state);
  }

  if (state.canvas.width !== width || state.canvas.height !== height) {
    state.canvas.width = width;
    state.canvas.height = height;
  }

  const now = Date.now();
  const shouldDetect = now - state.lastDetectTs >= VIDEO_DETECT_INTERVAL_MS || state.faces.length === 0;

  if (shouldDetect) {
    let detectSource: HTMLCanvasElement | HTMLVideoElement | HTMLCanvasElement = source;
    let scale = 1;

    const maxDim = Math.max(width, height);
    if (maxDim > VIDEO_MAX_DETECT_SIZE) {
      scale = VIDEO_MAX_DETECT_SIZE / maxDim;
      const dw = Math.max(1, Math.round(width * scale));
      const dh = Math.max(1, Math.round(height * scale));
      if (!state.detectCanvas) state.detectCanvas = document.createElement('canvas');
      state.detectCanvas.width = dw;
      state.detectCanvas.height = dh;
      const dctx = state.detectCanvas.getContext('2d');
      if (dctx) {
        dctx.drawImage(source, 0, 0, dw, dh);
        detectSource = state.detectCanvas;
      }
    }

    let faces = await detectFaces(detectSource as any);

    if (scale !== 1) {
      faces = faces.map(face => ({
        ...face,
        box: {
          ...face.box,
          x: face.box.x / scale,
          y: face.box.y / scale,
          width: face.box.width / scale,
          height: face.box.height / scale,
        },
        landmarks: face.landmarks ? {
          leftEye: face.landmarks.leftEye ? { x: face.landmarks.leftEye.x / scale, y: face.landmarks.leftEye.y / scale } : undefined,
          rightEye: face.landmarks.rightEye ? { x: face.landmarks.rightEye.x / scale, y: face.landmarks.rightEye.y / scale } : undefined,
          nose: face.landmarks.nose ? { x: face.landmarks.nose.x / scale, y: face.landmarks.nose.y / scale } : undefined,
          leftMouth: face.landmarks.leftMouth ? { x: face.landmarks.leftMouth.x / scale, y: face.landmarks.leftMouth.y / scale } : undefined,
          rightMouth: face.landmarks.rightMouth ? { x: face.landmarks.rightMouth.x / scale, y: face.landmarks.rightMouth.y / scale } : undefined,
        } : undefined,
      }));
    }

    const tracked = settings.trackFaces ? state.tracker.update(faces) : faces;

    const smoothed = tracked.map(face => {
      if (!settings.trackFaces || !face.trackId) return face;
      const smooth = state!.tracker.getSmoothedPosition(face.trackId);
      return smooth ? { ...face, box: smooth } : face;
    });
    const prevFaces = state.faces;

    state.lastDetectTs = now;
    if (smoothed.length === 0 && prevFaces.length > 0) {
      state.missingFrames += 1;
      state.faces = state.missingFrames > VIDEO_MAX_MISSING_FRAMES ? [] : prevFaces;
    } else {
      state.faces = smoothed;
      state.missingFrames = 0;
    }
  }

  if (!state.faces || state.faces.length === 0) return null;

  const ctx = state.canvas.getContext('2d');
  if (!ctx) return null;

  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);

  renderFaceBlurToContext(ctx, state.faces, settings, settings.selectedFaces);

  return state.canvas;
}

/**
 * Run face detection / tracking for a video element and return the current face regions.
 * This updates the internal videoStateCache (detection, scaling, tracking, smoothing)
 * but does NOT render the blur. Callers can use the returned regions to offload
 * blur rendering to a worker.
 *
 * Returns an empty array when detection finds no faces or settings are disabled.
 */
export async function detectVideoFaces(
  elementId: string,
  source: HTMLVideoElement | HTMLCanvasElement,
  settings: Partial<FaceBlurSettings>
): Promise<DetectedFace[]> {
  if (!settings.enabled) return [];

  const { width, height } = getSourceSize(source);
  if (!width || !height) return [];

  let state = videoStateCache.get(elementId);
  if (!state) {
    state = {
      canvas: document.createElement('canvas'),
      tracker: new FaceTracker(),
      faces: [],
      lastDetectTs: 0,
      missingFrames: 0,
    };
    videoStateCache.set(elementId, state);
  }

  const now = Date.now();
  const shouldDetect = now - state.lastDetectTs >= VIDEO_DETECT_INTERVAL_MS || state.faces.length === 0;

  if (shouldDetect) {
    let detectSource: HTMLCanvasElement | HTMLVideoElement = source;
    let scale = 1;

    const maxDim = Math.max(width, height);
    if (maxDim > VIDEO_MAX_DETECT_SIZE) {
      scale = VIDEO_MAX_DETECT_SIZE / maxDim;
      const dw = Math.max(1, Math.round(width * scale));
      const dh = Math.max(1, Math.round(height * scale));
      if (!state.detectCanvas) state.detectCanvas = document.createElement('canvas');
      state.detectCanvas.width = dw;
      state.detectCanvas.height = dh;
      const dctx = state.detectCanvas.getContext('2d');
      if (dctx) {
        dctx.drawImage(source, 0, 0, dw, dh);
        detectSource = state.detectCanvas;
      }
    }

    let faces = await detectFaces(detectSource as any);

    if (scale !== 1) {
      faces = faces.map(face => ({
        ...face,
        box: {
          ...face.box,
          x: face.box.x / scale,
          y: face.box.y / scale,
          width: face.box.width / scale,
          height: face.box.height / scale,
        },
        landmarks: face.landmarks ? {
          leftEye: face.landmarks.leftEye ? { x: face.landmarks.leftEye.x / scale, y: face.landmarks.leftEye.y / scale } : undefined,
          rightEye: face.landmarks.rightEye ? { x: face.landmarks.rightEye.x / scale, y: face.landmarks.rightEye.y / scale } : undefined,
          nose: face.landmarks.nose ? { x: face.landmarks.nose.x / scale, y: face.landmarks.nose.y / scale } : undefined,
          leftMouth: face.landmarks.leftMouth ? { x: face.landmarks.leftMouth.x / scale, y: face.landmarks.leftMouth.y / scale } : undefined,
          rightMouth: face.landmarks.rightMouth ? { x: face.landmarks.rightMouth.x / scale, y: face.landmarks.rightMouth.y / scale } : undefined,
        } : undefined,
      }));
    }

    const tracked = settings.trackFaces ? state.tracker.update(faces) : faces;

    const smoothed = tracked.map(face => {
      if (!settings.trackFaces || !face.trackId) return face;
      const smooth = state!.tracker.getSmoothedPosition(face.trackId);
      return smooth ? { ...face, box: smooth } : face;
    });
    const prevFaces = state.faces;

    state.lastDetectTs = now;
    if (smoothed.length === 0 && prevFaces.length > 0) {
      state.missingFrames += 1;
      state.faces = state.missingFrames > VIDEO_MAX_MISSING_FRAMES ? [] : prevFaces;
    } else {
      state.faces = smoothed;
      state.missingFrames = 0;
    }
  }

  return state.faces ?? [];
}

/**
 * Clear cache for an element
 */
export function clearFaceBlurCache(elementId: string): void {
  faceCache.delete(elementId);
  processedImageCache.delete(elementId);
  videoStateCache.delete(elementId);
}

/**
 * Get detected faces for an element (for UI display)
 */
export function getDetectedFaces(elementId: string): DetectedFace[] | null {
  const cached = faceCache.get(elementId);
  return cached?.faces || null;
}

/**
 * Check if face blur processing is needed
 */
export function needsFaceBlurProcessing(settings: Partial<FaceBlurSettings> | null | undefined): boolean {
  return !!settings?.enabled;
}
